import { type TrackEventPayload } from '@atlassiansox/analytics-web-client';

import { getCommitTimestamp } from '@canvas/buildtime-constants';
import { currentOrgData } from '@canvas/canvas-state/org-data';
import { dynamicImport } from '@canvas/dynamic-import';
import { getSessionDataUnvalidated } from '@canvas/frame-api/get-session-data';
import { canvasFrameAPI } from '@canvas/frame-api/host-api/host-frame-context';
import { isNonAnonymous } from '@canvas/frame-api/is-anonymous';

import { analyticsContext } from './analytics-context.ts';

export type ConfluenceTrackAnalyticsEvent = TrackEventPayload;

export const fireConfluenceTrackEvent = async (event: ConfluenceTrackAnalyticsEvent) => {
  if (!analyticsContext.client) {
    return;
  }

  try {
    const analyticsWebClientModule = await dynamicImport(
      () => import('@atlassiansox/analytics-web-client'),
    );
    const AnalyticsWebClient = analyticsWebClientModule.default;
    const { envType, tenantType, userType } = analyticsWebClientModule;

    const sessionData = getSessionDataUnvalidated();
    const env =
      process.env.VITE_CANVAS_ANALYTICS_ENV ||
      (process.env.MODE?.startsWith('prod-') ? envType.PROD : envType.STAGING);

    const confluenceClient = new AnalyticsWebClient(
      {
        env,
        product: 'confluence',
        version: getCommitTimestamp()?.toString() ?? 'unknown',
      },
      { useLegacyUrl: true },
    );

    if (sessionData.tenantId) {
      confluenceClient.setTenantInfo(tenantType.CLOUD_ID, sessionData.tenantId);
    }
    if (isNonAnonymous() && sessionData.accountId) {
      confluenceClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, sessionData.accountId);
    }

    const orgId = currentOrgData.id ?? canvasFrameAPI?.getOrgId?.();
    if (orgId) {
      confluenceClient.setOrgInfo(orgId);
    }

    await confluenceClient.sendTrackEvent(event);
  } catch (e) {
    console.error(e);
  }
};
